import "@src/shared/utils/faker/extensions";
import "@src/shared/utils/faker/seed";

import "@el8/vital/dist/vital.css";
import "@src/shared/utils/http/initApiClient";
import "@src/shared/utils/msw/initMSW";
import "@src/shared/utils/observability/datadog";
import "@src/shared/utils/observability/sentry";

import "@src/app.css";

import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { IS_IN_IFRAME } from "@src/env";
import { ErrorBoundaryV2, RootV2Route } from "@src/features/auth/v2";
import { IsChromeExtensionProvider } from "@src/features/chrome";

import { ErrorBoundary } from "../../deprecated/routes/error-boundary";
import Root from "../../deprecated/routes/root";
import Status from "../../deprecated/routes/status";
import { queryClient } from "../../queryClient";
import { useStitchContainer } from "../../shared/hooks/useStitchContainer";
import { ReactQueryClientProvider } from "../../shared/providers/reactQuery";
import { UrlContextProvider } from "../../shared/providers/urlContext";
import { createV1Routes } from "../v1Routes";
import { createV2Routes } from "../v2Routes";

function createRouter() {
  const router = createBrowserRouter([
    {
      id: "root",
      path: "/",
      element: <Root />,
      errorElement: IS_IN_IFRAME ? <ErrorBoundary /> : <ErrorBoundaryV2 />,
      children: [
        {
          path: "status",
          element: <Status />,
        },
        ...(IS_IN_IFRAME
          ? createV1Routes()
          : [
              {
                element: <RootV2Route />,
                children: createV2Routes(),
              },
            ]),
      ],
    },
  ]);

  return router;
}

const root = document.getElementById("root");

/**
 * Web Application (Data Mode)
 *
 * Uses createBrowserRouter for client-side routing with data loading.
 * Authentication is handled by Better Auth (cookie session); see `auth/client.ts`.
 *
 * The Elation iframe embed (`IS_IN_IFRAME`) mounts the deprecated v1 route tree
 * so chart patient-open still lands on briefing/today. Standalone web keeps v2.
 * The root error boundary follows the mounted tree so its recovery links point
 * at URLs that tree actually serves.
 */
function App() {
  useStitchContainer();

  const router = useMemo(() => createRouter(), []);

  return (
    <StrictMode>
      <div id="stitch-container">
        <IsChromeExtensionProvider isChromeExtension={false}>
          <ReactQueryClientProvider queryClient={queryClient}>
            <UrlContextProvider>
              <RouterProvider router={router} />
            </UrlContextProvider>
          </ReactQueryClientProvider>
        </IsChromeExtensionProvider>
      </div>
    </StrictMode>
  );
}

if (root) {
  createRoot(root).render(<App />);
}
